import React, { useState, useContext, Fragment, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Navbar } from 'react-bootstrap';
import Select from 'react-select';
import Loading from './ui-elements/Loading';

import { LoginMenu } from './api-authorization/LoginMenu';
import InfoPanel from './InfoPanel';
import ProcessContext from '../contexts/process/ProcessContext';
import AuthContext from '../contexts/auth/AuthContext';
import { TENANT_ADMIN, OPERATOR, MARKETING } from '../constants/roles/roles';
import HelpDeskIconButton from './layout/HelpDeskIconButton';
import Tickets from './layout/Tickets';

export const NavMenu = () => {
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState(null);
  const [adminLinks, setAdminLinks] = useState([]);

  const toggleNavbar = () => {
    setCollapsed((prev) => !prev);
  };

  const {
    currentContainer,
    setCurrentContainer,
    currentTenant,
    userInformation,
    setCurrentTenant,
    currentRoles,
    accessibleContainers,
    accessibleTenants,
    isGlobalAdmin,
    seedTypesLoading
  } = useContext(AuthContext);
  const { processStarted } = useContext(ProcessContext);

  // Re-evaluates whenever roles OR the selected container changes
  useEffect(() => {
    if (currentRoles || isGlobalAdmin) {
      setAdminLinks(getAdminLinks());
    }
  }, [currentRoles, isGlobalAdmin, currentContainer]);

  const containerOptions = React.useMemo(() => {
    return accessibleContainers?.map((container) => ({
      label: container.name,
      value: container.id,
    }));
  }, [userInformation, accessibleContainers, currentTenant, currentContainer]);

  const tenantOptions = React.useMemo(() => {
    const currentTenantId = currentTenant?.id;
    return (accessibleTenants ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tenant) => ({
        label: tenant.name,
        value: tenant.id,
        isDisabled: tenant.id === currentTenantId
      }));
  }, [userInformation, accessibleTenants, currentTenant?.id]);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    const initializeLailo = () => {
      if (window.Lailo) {
        window.Lailo.initHalfScreenWidget({
          botSecret: import.meta.env.VITE_LAILO_BOT_SECRET,
          language: "en-US",
          generalSettings: { initAvatar: false },
          colorSettings: {
            primary: "#464646",
            secondary: "#464646",
            inputFieldBackground: "#e4e4e4",
            inputFieldText: "#464646"
          }
        });
      }
    };

    if (window.Lailo) {
      initializeLailo();
    } else {
      window.onload = initializeLailo;
    }
    document.addEventListener('lailoInitialized', (e) => { console.log('Lailo successfully initialised', e) });
  }, []);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, [window.location.pathname]);

  const Separator = () => (
    <hr
      style={{
        height: 3,
        margin: 0,
        padding: 0,
        top: -12,
        backgroundColor: 'grey',
      }}
    />
  );

  const managementOptions = [
    {
      label: <span><i className='fa-solid fa-house icon-color' ></i>Containers</span>,
      value: '/management/containers/list',
      onClick: () => navigate('/management/containers/list'),
      role: [isGlobalAdmin, TENANT_ADMIN],
    },
    {
      label: <span><i className='fa-solid fa-print icon-color' ></i>Printer Templates</span>,
      value: '/templates',
      onClick: () => navigate('/templates'),
      role: [isGlobalAdmin, TENANT_ADMIN]
    },
    {
      label: <span><i className='fa-solid fa-seedling icon-color' ></i>Seed Types</span>,
      value: '/management/seed-types/list',
      onClick: () => navigate('/management/seed-types/list'),
      role: [isGlobalAdmin, TENANT_ADMIN, OPERATOR],
      hideIfNoContainer: true
    },
    {
      label: <span><i className='fa-solid fa-gift icon-color' ></i>Produce Types</span>,
      value: '/management/produce-types/list',
      onClick: () => navigate('/management/produce-types/list'),
      role: [isGlobalAdmin, TENANT_ADMIN, OPERATOR, MARKETING]
    },
    {
      label: <Separator />,
      value: 'seperator',
      isDisabled: true,
      role: []
    },
    {
      label: <span><i className='fa-solid fa-building icon-color' ></i>Tenants</span>,
      value: '/tenantmanagement',
      onClick: () => navigate('/tenantmanagement'),
      role: [isGlobalAdmin]
    },
    {
      label: <span><i className='fa-solid fa-user icon-color' ></i>Tenant Users</span>,
      value: '/management/tenantUsers',
      onClick: () => {
        navigate('/management/tenantusers');
      },
      role: [isGlobalAdmin, TENANT_ADMIN]
    },
    {
      label: <span><i className='fa-solid fa-user-plus icon-color' ></i>Global Users</span>,
      value: '/globalusermanagement',
      onClick: () => {
        navigate('/globalusermanagement');
      },
      role: [isGlobalAdmin]
    }
  ];

  const getAdminLinks = () => {
    const options = [];
    managementOptions.forEach((option) => {
      if (option.hideIfNoContainer && (!currentContainer || accessibleContainers?.length < 1)) {
        return;
      }

      if (isGlobalAdmin) {
        return options.push(option);
      }
      if (option.value === 'seperator') {
        return options.push(option);
      }
      if (currentRoles.some(item => option.role.includes(item))) {
        return options.push(option);
      };
    });

    const indexOfSeperator = options.findIndex(o => o.value === 'seperator');
    if (indexOfSeperator === 0 || indexOfSeperator === options.length - 1) {
      options.splice(indexOfSeperator, 1);
    }
    return options;
  }

  const selectStyleLeft = {
    container: (provided) => ({ ...provided, minWidth: '130px', backgroundColor: '--nav-option-background-inactive' }),
    control: (provided) => ({ ...provided, backgroundColor: '--nav-option-background-inactive', border: 'none', justifyContent: 'unset', fontWeight: 700 }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorContainer: () => ({ display: 'none' }),
    indicatorsContainer: () => ({ display: 'none' }),
    valueContainer: (provided) => ({ ...provided, flex: 'unset' }),
    menu: (provided) => ({ ...provided, width: 'max-content', overflow: 'hidden' }),
    option: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'var(--nav-option-text-inactive)' : 'var(--nav-option-text-active)',
      backgroundColor: state.isDisabled ? 'var(--nav-option-bg-inactive)' : (state.isFocused || state.isSelected ? 'var(--nav-option-background-active)' : 'var(--nav-option-background-inactive)'),
    }),
  };

  const selectStyleRight = {
    ...selectStyleLeft,
    indicatorSeparator: () => ({ display: 'block' }),
    menu: (provided) => ({ ...provided, width: 'max-content', overflow: 'none', textAlign: 'left' }),
    option: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'var(--nav-option-text-inactive)' : 'var(--nav-option-text-active)',
      backgroundColor: state.isDisabled ? 'var(--nav-option-bg-inactive)' : (state.isFocused || state.isSelected ? 'var(--nav-option-background-active)' : 'var(--nav-option-background-inactive)'),
      padding: state.isDisabled ? '0px 20px' : '8px 20px',
      margin: '2px 0px',
      height: state.isDisabled ? 10 : 40
    }),
  };

  const defaultContainerValue = React.useMemo(() => {
    return currentContainer
      ? containerOptions?.find((option) => option.value == currentContainer?.id)
      : containerOptions?.find((option) => option.value == containerOptions[0]);
  }, [currentContainer, containerOptions]);

  const defaultTenantValue = React.useMemo(() => {
    return tenantOptions?.find((o) => o.value === currentTenant?.id) ?? tenantOptions?.[0];
  }, [tenantOptions, currentTenant?.id]);

  const defaultLinkName = { label: 'Management', value: '/' };

  return (seedTypesLoading ? <Loading fullScreen={true} /> :
    <header>
      <Navbar className='wide-navbar' bg='light' expand='sm'>
        <Container className='navmenu-container'>
          <Navbar.Brand as={Link} to="/" state={{ slide: 0 }}>FarmerApp</Navbar.Brand>
          {(userInformation && accessibleTenants?.length > 0) && (
            <Select
              key={currentTenant?.id || 'no-tenant'}
              isSearchable={false}
              options={tenantOptions}
              styles={selectStyleLeft}
              isDisabled={processStarted || currentPath !== '/'}
              placeholder={'Select Tenant'}
              value={defaultTenantValue}
              noOptionsMessage={() => 'No Tenants Found'}
              onChange={async (option) => {
                if (!option || option.value === currentTenant?.id) return;
                await setCurrentTenant(accessibleTenants?.find((t) => t.id == option.value))
              }}
            />
          )}

          {(userInformation && accessibleContainers.length > 0) && (
            <Select
              options={containerOptions}
              isSearchable={false}
              styles={selectStyleLeft}
              isDisabled={processStarted || currentPath !== '/'}
              placeholder={'Select Container'}
              value={defaultContainerValue}
              noOptionsMessage={() => 'No Containers Found'}
              onChange={async (option) =>
                await setCurrentContainer(accessibleContainers?.find((c) => c.id == option.value))
              }
            />
          )}
          <Navbar.Toggle onClick={toggleNavbar} className='mr-2' />
          <Navbar.Collapse className='justify-content-end'>
            <ul className='navbar-nav align-items-center'>
              <Fragment>
                { userInformation && <Select
                  options={adminLinks}
                  isSearchable={false}
                  styles={selectStyleRight}
                  value={defaultLinkName}
                  onChange={(option) => option.onClick()}
                  placeholder='Administration'
                  maxMenuHeight={320}
                />}
              </Fragment>
              {(userInformation && accessibleContainers.length > 0) && <Tickets />}
              {(userInformation && accessibleContainers.length > 0) && <HelpDeskIconButton />}
              <LoginMenu />
            </ul>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <InfoPanel />
    </header>
  );
};